<template>
  <div :class="$style.container">
    <div
      v-if="badStateDevices"
      :class="$style.badStateBanner"
    >
      <SecureTokenRemediationBanner
        :setLoadingOverlay="setLoadingOverlay"
        @click="handleTabClick"
      />
    </div>
    <div
      v-if="unacknowledgedDevices"
      :class="$style.badStateBanner"
    >
      <UnmanagedDevicesBanner
        :currentTabIndex="tabIndex"
        :totalCount="totalUnmanagedCount"
        @click="handleTabClick"
      />
    </div>
    <SystemsMainPanelTabs
      v-if="showTabs"
      :activeSelection="tabIndex"
      :badStateDevices="badStateDevices"
      :unacknowledgedDevices="unacknowledgedDevices"
      @click="handleTabClick"
    />
    <div
      v-show="tabIndex === 0"
      :class="[$style.container, {
        [$style.emptyContainer]: totalCount === 0,
      }]"
      data-test-id="SystemsMainPanel_All"
    >
      <SystemItemTable
        ref="itemTable"
        :asidePanelCanceledEventName="legacyAsidePanelCanceledEventName"
        :canModifyColumns="true"
        :columnOptions="columnOptions"
        :columns="columnsToDisplay"
        :defaultColumns="defaultColumns"
        :defaultSortField="defaultItemTableSort"
        :isAsideOpen="isAsideOpen"
        :itemType="itemType"
        :selectedModelId="selectedModelId"
        :selectionItemText="{
          label: 'selected',
          plural: deviceItem.labelPlural,
          singular: itemType,
        }"
        :setGettingStartedTooltipVisibility="showTooltip"
        :setSelectedModelId="setSelectedModelId"
        :useDropDownFilterComponent="FilterComponentConsts.SystemDropDownFilter"
        :useSearchContextFilterComponent="FilterComponentConsts.SystemSearchFilter"
        :viewport="viewport"
        @columnsChange="handleColumnsChange"
        @revertColumns="handleRevertColumns"
      />
    </div>
    <div
      v-show="tabIndex === 1"
      :class="$style.container"
    >
      <NeedsAttentionTable
        :isAsideOpen="isAsideOpen"
        :itemType="itemType"
        :selectedModelId="selectedModelId"
        :setSelectedModelId="setSelectedModelId"
        :viewport="viewport"
      />
    </div>
    <div
      v-show="tabIndex === 2"
      :class="$style.container"
    >
      <UnacknowledgedSystemItemTable
        :isAsideOpen="isAsideOpen"
        :itemType="itemType"
        :selectedModelId="selectedModelId"
        :setSelectedModelId="setSelectedModelId"
        :viewport="viewport"
      />
    </div>
  </div>
</template>

<script>
import { FilterComponentConsts, ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { mapGetters, mapState } from 'vuex';
import { sortBy } from 'underscore';
import ItemType from 'client/util/Constants/ItemType';
import LocalStorageConsts from 'client/util/Constants/LocalStorageConstants';
import MainPanelMixin from 'components/MainPanel/mixins/MainPanelMixin';
import MainPanelWithItemTableMixin from 'components/MainPanel/mixins/MainPanelWithItemTableMixin';
import NeedsAttentionTable from 'client/components/ItemTable/NeedsAttentionItemTable.vue';
import SecureTokenRemediationBanner from 'client/components/ItemTable/SecureTokenRemediationBanner.vue';
import SystemItemTable from 'client/systems/SystemItemTable/SystemItemTable.vue';
import SystemItemTableColumns, { mainPanelSystemItemTableColumnOptions } from 'client/systems/SystemItemTable/SystemItemTableColumns';
import SystemsMainPanelTabs from 'client/components/MainPanel/MainPanelComponents/SystemsMainPanelTabs.vue';
import UnacknowledgedSystemItemTable from 'components/ItemTable/UnacknowledgedSystemItemTable.vue';
import UnmanagedDevicesBanner from 'client/components/ItemTable/UnmanagedDevicesBanner.vue';

const { system, systemUnacknowledged } = ItemTypeConsts;

const systemUnacknowledgedCollection = StoreModuleMap[systemUnacknowledged];

const { DeviceItemType } = ItemType;

const systemCollection = StoreModuleMap[system];

export default {
  name: 'SystemsMainPanel',

  components: {
    NeedsAttentionTable,
    SecureTokenRemediationBanner,
    SystemsMainPanelTabs,
    SystemItemTable,
    UnacknowledgedSystemItemTable,
    UnmanagedDevicesBanner,
  },

  mixins: [MainPanelMixin, MainPanelWithItemTableMixin],

  data: () => ({
    availableColumns: SystemItemTableColumns,
    badStateDevices: false,
    unacknowledgedDevices: false,
    defaultColumns: [
      SystemItemTableColumns.status,
      SystemItemTableColumns.name,
      SystemItemTableColumns.os,
      SystemItemTableColumns.primaryAdapterIp,
      SystemItemTableColumns.created,
      SystemItemTableColumns.mfaStatus,
      SystemItemTableColumns.lastContact,
    ],
    deviceItem: new DeviceItemType(),
    FilterComponentConsts,
    localStorageKey: LocalStorageConsts.systemMainPanelColumns,
    showLoadingOverlay: false,
    tabIndex: 0,
  }),

  computed: {
    ...mapGetters(systemUnacknowledgedCollection, {
      totalUnmanagedCount: 'getTotalCount',
    }),

    ...mapState(systemCollection, {
      totalCount: (state) => state.entities.items.totalCountOfAllItems,
    }),

    columnOptions() {
      const requiredFields = [
        SystemItemTableColumns.status.name,
        SystemItemTableColumns.name.name,
      ];

      const sortedOptions = sortBy(mainPanelSystemItemTableColumnOptions, 'name');

      return sortedOptions.map(column => ({
        name: column.name,
        value: column.name,
        required: requiredFields.includes(column.name),
      }));
    },

    defaultItemTableSort() {
      const systemColumnsValue = localStorage.getItem(
        LocalStorageConsts.systemMainPanelColumns,
      );
      return systemColumnsValue
        && !systemColumnsValue.includes(SystemItemTableColumns.created.name) ? '' : '-created';
    },

    showTabs() {
      return this.badStateDevices || this.unacknowledgedDevices;
    },
  },

  async mounted() {
    [this.badStateDevices, this.unacknowledgedDevices] = [await
    this.hasBadStateDevices(), await this.hasUnacknowledgedSystems(),
    ];
  },

  created() {
    this.legacyAsidePanelCanceledEventName = 'system-details:canceled';
  },

  methods: {
    ...mapGetters(systemCollection, [
      'hasBadStateDevices',
      'hasUnacknowledgedSystems',
    ]),

    handleTabClick(index) {
      this.tabIndex = index;
    },

    setLoadingOverlay(loadingState) {
      this.showLoadingOverlay = loadingState;
    },
  },
};
</script>

<style module>
@value (jcWhite) from '~css/brand-colors.css';

.badStateBanner {
  padding: 20px 20px 0;
}

.container {
  background: jcWhite;
  height: 100%;
}

.emptyContainer {
  height: auto;
}

</style>
